"use strict";

// Source-engine style developer console for Krunker, ported from
// Krunker_v3.1.1/mod/devConsole.ts.
// ─────────────────────────────────────────────────────────
// • Toggle key (default backtick) is wired in index.js via
//   window.pressButton -> window.devConsole.toggle().
// • Type a command, press Enter. Up/Down navigates history.
// • Tab completes the current token.
// • An argument hint row renders under the input as you type.
// • Output area mirrors console.log/warn/error and, once init()
//   receives the io event table, incoming chat (`ch`).
// • Submitted commands echo as `] <cmd>`.
//
// Access model: unlike v3.1.1 (where the mod bundle shared the
// engine's global scope), v1.5.2 keeps its singletons module-scoped
// inside index.js's IIFE. So the faithful analog is to inject the
// real singletons via init(deps) and read them lazily, then expose
// window.devConsole the same way. `player` is passed as a getter
// because index.js reassigns it on death/respawn.
// ─────────────────────────────────────────────────────────

// ─── Injected singletons (set at init) ──────────────────
var _Game = null;
var _getPlayer = function () { return null; };
var _CONTROLS = null;
var _config = null;
var _io = null;
var _RENDER = null;
var _UI = null;
var _events = null;
var _playCustomMap = null;

function player() { return _getPlayer ? _getPlayer() : null; }

// ─── DOM scaffolding ────────────────────────────────────

var style = document.createElement("style");
style.textContent = `
#devConsoleOverlay {
  position: fixed;
  inset: 0;
  display: none;
  z-index: 999999;
  background: transparent;
  pointer-events: none;
}
#devConsoleOverlay * {
  font-family: "Lucida Console", "Consolas", "Liberation Mono", "Courier New", monospace;
  -webkit-font-smoothing: none;
  -moz-osx-font-smoothing: grayscale;
  font-smooth: never;
  text-rendering: geometricPrecision;
}
#devConsoleOverlay .modal {
  opacity: 0.95;
  position: absolute;
  right: 8px;
  top: 8px;
  padding: 7px;
  border-radius: 5px;
  width: min(744px, calc(100vw - 16px));
  height: min(280px, calc(100vh - 16px));
  background: #7a7a7a;
  display: flex;
  flex-direction: column;
  color: #d0d0d0;
  font-size: 12px;
  pointer-events: auto;
  min-width: 280px;
  min-height: 160px;
}
#devConsoleOverlay .titlebar {
  background: #7a7a7a;
  font-family: inherit;
  font-size: 10px;
  font-weight: normal;
  padding: 3px 6px;
  user-select: none;
  cursor: move;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 18px;
  flex: 0 0 auto;
}
#devConsoleOverlay .titlebar span { color: #fff; font-weight: bold; }
#devConsoleOverlay .titlebar .x {
  width: 18px;
  height: 16px;
  background: transparent;
  border: 0;
  color: #d0d0d0;
  font-family: inherit;
  font-size: 10px;
  line-height: 14px;
  text-align: center;
  cursor: pointer;
  user-select: none;
}
#devConsoleOverlay .titlebar .x:hover { color: #fff; }
#devConsoleOverlay .body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 6px 6px 5px;
  gap: 2px;
  overflow: hidden;
  background: #7a7a7a;
}
#devConsoleOverlay .logs {
  flex: 1 1 auto;
  overflow-y: scroll;
  background: #6a6a6a;
  border: 1px #282828 inset;
  padding: 3px 5px;
  white-space: pre-wrap;
  word-break: break-word;
  color: #e0e0e0;
  line-height: 1.25;
  font-family: inherit;
  font-size: 12px;
}
#devConsoleOverlay .logs, #devConsoleOverlay .logs * {
  user-select: text !important;
  -webkit-user-select: text !important;
  cursor: text;
}
#devConsoleOverlay .logs ::selection,
#devConsoleOverlay .logs::selection {
  background: #f39c00;
  color: #000000;
}
#devConsoleOverlay .logs::-webkit-scrollbar { width: 14px; }
#devConsoleOverlay .logs::-webkit-scrollbar-track { background: #4a4a4a; }
#devConsoleOverlay .logs::-webkit-scrollbar-thumb {
  background: #7a7a7a;
  border: 1px solid #3a3a3a;
}
#devConsoleOverlay .hints {
  position: absolute;
  left: 0;
  right: 70px;
  bottom: calc(100% + 1px);
  display: none;
  max-height: 180px;
  overflow-y: auto;
  background: #6f6f6f;
  border: 1px solid #4c4c4c;
  color: #d8d8d8;
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
  z-index: 2;
}
#devConsoleOverlay .hints.isOpen { display: block; }
#devConsoleOverlay .hintItem { padding: 2px 6px; cursor: default; }
#devConsoleOverlay .hintItem.isActive { background: #5f5f5f; color: #ffffff; }
#devConsoleOverlay .hintMeta { color: #b8b8b8; }
#devConsoleOverlay .inputShell { position: relative; flex: 0 0 auto; margin-top: 1px; }
#devConsoleOverlay .row { display: flex; gap: 6px; align-items: stretch; }
#devConsoleOverlay .row input {
  flex: 1 1 auto;
  background: #585858;
  outline: 0;
  color: #e0e0e0;
  font-family: inherit;
  font-size: 12px;
  padding: 3px 5px;
  min-width: 0;
  border: 1px #282828 inset;
}
#devConsoleOverlay .row button[type="submit"] {
  background-color: #7a7a7a;
  width: 64px;
  border: 1px #a2a2a2 outset;
  font-size: 10px;
}
#devConsoleOverlay .row button {
  background: #7a7a7a;
  border: 1px solid #3a3a3a;
  font-family: inherit;
  font-size: 12px;
  padding: 3px 0;
  cursor: pointer;
  color: #e0e0e0;
}
#devConsoleOverlay .row button:hover { background: #8a8a8a; }
#devConsoleOverlay .row button:active { background: #6a6a6a; }
#devConsoleOverlay .grip {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: nwse-resize;
  background:
    linear-gradient(135deg, transparent 0 6px, #9a9a9a 6px 7px, transparent 7px 9px, #9a9a9a 9px 10px, transparent 10px 12px, #9a9a9a 12px 13px, transparent 13px);
}
`;
(document.head || document.documentElement).appendChild(style);

var overlay = document.createElement("div");
overlay.id = "devConsoleOverlay";

var modal = document.createElement("div");
modal.className = "modal";

var header = document.createElement("div");
header.className = "titlebar";
var headerLabel = document.createElement("span");
headerLabel.textContent = "Console";
var closeBtn = document.createElement("span");
closeBtn.className = "x";
closeBtn.textContent = "✕";
closeBtn.addEventListener("click", function () { toggleConsole(false); });
header.append(headerLabel, closeBtn);

var body = document.createElement("div");
body.className = "body";

var logs = document.createElement("div");
logs.className = "logs";
logs.tabIndex = -1;
logs.style.outline = "none";
logs.addEventListener("mousedown", function () {
  setTimeout(function () { logs.focus({ preventScroll: true }); }, 0);
});
logs.addEventListener("keydown", function (e) {
  if ((e.ctrlKey || e.metaKey) && (e.key === "a" || e.key === "A")) {
    e.preventDefault();
    e.stopPropagation();
    var sel = window.getSelection();
    var range = document.createRange();
    range.selectNodeContents(logs);
    sel.removeAllRanges();
    sel.addRange(range);
  }
});

var hints = document.createElement("div");
hints.className = "hints";

var inputShell = document.createElement("div");
inputShell.className = "inputShell";

// A <form> so pressing Enter dispatches `submit`, which survives an
// upstream capture-phase keydown listener that stopImmediatePropagation.
var inputRow = document.createElement("form");
inputRow.className = "row";

var input = document.createElement("input");
input.type = "text";
input.autocomplete = "off";
input.spellcheck = false;

var submitBtn = document.createElement("button");
submitBtn.type = "submit";
submitBtn.textContent = "Submit";

var grip = document.createElement("div");
grip.className = "grip";

inputRow.append(input, submitBtn);
inputShell.append(hints, inputRow);
body.append(logs, inputShell);
modal.append(header, body, grip);
overlay.append(modal);

function attach() { return document.body && document.body.appendChild(overlay); }
if (document.body) attach();
else document.addEventListener("DOMContentLoaded", attach, { once: true });

// ─── Output sink ────────────────────────────────────────

var COLORS = {
  log: "#e0e0e0",
  info: "#9ac8ff",
  warn: "#ffb84d",
  error: "#ff6a6a",
  cmd: "#ffffff",
  reply: "#9affb0",
  hint: "#b0b0b0",
};

function append(text, kind) {
  kind = kind || "log";
  var line = document.createElement("div");
  line.style.color = COLORS[kind] || COLORS.log;
  line.textContent = String(text);
  var atBottom = logs.scrollTop + logs.clientHeight >= logs.scrollHeight - 4;
  logs.appendChild(line);
  while (logs.childElementCount > 1000) logs.removeChild(logs.firstChild);
  if (atBottom) logs.scrollTop = logs.scrollHeight;
}

function print(text) { append(text, "log"); }

// Mirror console.* — keep originals intact for devtools.
["log", "info", "warn", "error"].forEach(function (k) {
  var orig = console[k].bind(console);
  console[k] = function () {
    var args = Array.prototype.slice.call(arguments);
    try {
      var text = args.map(function (a) {
        if (typeof a === "string") return a;
        try { return JSON.stringify(a); } catch (e) { return String(a); }
      }).join(" ");
      append(text, k);
    } catch (e) { /* never let logging crash the game */ }
    orig.apply(null, args);
  };
});

// ─── Command registry ───────────────────────────────────

var cmds = Object.create(null);

function arg(name, type, optional) {
  return { name: name, type: type || "string", optional: !!optional };
}

function registerLocal(name, args, desc, run) {
  cmds[name] = { kind: "local", args: args, desc: desc, run: run };
}

function registerServer(name, args, desc) {
  cmds[name] = { kind: "server", args: args, desc: desc };
}

// ── State ──────────────────────────────────────────────

var history = [];
var historyIdx = -1;
var draft = "";
var keyBinds = Object.create(null);
var aliases = Object.create(null);

// ── Local commands ─────────────────────────────────────

registerLocal("help", [arg("cmd", "string", true)], "list commands", function (a) {
  if (a[0]) {
    var c = cmds[a[0]];
    if (!c) return append("unknown command: " + a[0], "error");
    append(a[0] + " " + fmtArgs(c.args) + " — " + c.desc, "info");
    return;
  }
  var names = Object.keys(cmds).sort();
  append(names.length + " commands:", "info");
  for (var i = 0; i < names.length; i++) {
    append("  " + names[i].padEnd(18) + " " + cmds[names[i]].desc, "hint");
  }
});

registerLocal("clear", [], "clear console output", function () {
  logs.innerHTML = "";
});

registerLocal("echo", [arg("text", "string")], "print text to console", function (a) {
  append(a.join(" "));
});

registerLocal("print", [arg("text", "string")], "alias for echo", function (a) {
  append(a.join(" "));
});

registerLocal("bind",
  [arg("key", "string", true), arg("command", "string", true)],
  "bind a key to a command (session only)",
  function (a) {
    if (!a[0]) {
      var keys = Object.keys(keyBinds);
      if (!keys.length) return append("no binds", "hint");
      for (var i = 0; i < keys.length; i++) append("  " + keys[i] + " = " + keyBinds[keys[i]], "hint");
      return;
    }
    if (!a[1]) {
      append(a[0] + " = " + (keyBinds[a[0]] || "(unbound)"), "info");
      return;
    }
    keyBinds[a[0]] = a.slice(1).join(" ");
    append("bound " + a[0] + " -> " + keyBinds[a[0]], "info");
  }
);

registerLocal("unbind", [arg("key", "string")], "remove a key bind", function (a) {
  if (!a[0]) return;
  delete keyBinds[a[0]];
  append("unbound " + a[0], "info");
});

registerLocal("alias",
  [arg("name", "string", true), arg("command", "string", true)],
  "define a command alias",
  function (a) {
    if (!a[0]) {
      var keys = Object.keys(aliases);
      if (!keys.length) return append("no aliases", "hint");
      for (var i = 0; i < keys.length; i++) append("  " + keys[i] + " = " + aliases[keys[i]], "hint");
      return;
    }
    if (!a[1]) {
      append(a[0] + " = " + (aliases[a[0]] || "(undefined)"), "info");
      return;
    }
    var b = a.slice(1).join(" ");
    aliases[a[0]] = b;
    registerLocal(a[0], [], "alias for: " + b, function () { exec(b); });
    append("alias " + a[0] + " -> " + b, "info");
  }
);

registerLocal("exec", [arg("commands", "string")], "run multiple commands separated by ;", function (a) {
  var parts = a.join(" ").split(";");
  for (var i = 0; i < parts.length; i++) {
    var t = parts[i].trim();
    if (t) exec(t);
  }
});

registerLocal("quit", [], "close the window", function () { window.close(); });

registerLocal("reconnect", [], "reload the current page", function () { location.reload(); });

registerLocal("version", [], "show current client version", function () {
  append("Version: " + (window.gameVersion || (_config && _config.version) || "(unknown)"), "info");
});

registerLocal("build", [], "alias for version", function () {
  append("Version: " + (window.gameVersion || (_config && _config.version) || "(unknown)"), "info");
});

registerLocal("say", [arg("text", "string")], "say something in chat", function (a) {
  var text = a.join(" ").trim();
  if (!text) return;
  sendServer(text);
});

registerLocal("pos", [], "show local player position", function () { printLocalPosition(); });
registerLocal("position", [], "alias for pos", function () { printLocalPosition(); });
registerLocal("rot", [], "show local player rotation", function () { printLocalRotation(); });
registerLocal("rotation", [], "alias for rot", function () { printLocalRotation(); });

registerLocal("ping", [], "show current ping", function () {
  var p = player();
  var pingSamples = p && Array.isArray(p.pings) ? p.pings : [];
  var latest = p && Number.isFinite(p.ping) ? Math.max(0, Math.round(p.ping)) : null;
  var avg = pingSamples.length
    ? Math.round(pingSamples.reduce(function (sum, s) { return sum + (Number(s) || 0); }, 0) / pingSamples.length)
    : null;
  var resolved = latest != null ? latest : (avg != null ? avg : 0);
  append("Ping: " + resolved + "ms", "info");
});

registerLocal("status", [], "show current connection details", function () { printServerDetails(); });
registerLocal("serverinfo", [], "alias for status", function () { printServerDetails(); });

function formatTransformValue(value) {
  var num = Number(value);
  return Number.isFinite(num) ? num.toFixed(6) : "0.000000";
}

function printLocalPosition() {
  var p = player();
  if (!p) return append("no local player", "error");
  append("position: " + formatTransformValue(p.x) + " " +
    formatTransformValue(p.y) + " " + formatTransformValue(p.z), "info");
}

function printLocalRotation() {
  var p = player();
  if (!p) return append("no local player", "error");
  // v1.5.2 Player has no yaw/pitch; use xDire for yaw and the camera
  // pitch object on CONTROLS for pitch.
  var pitch = _CONTROLS && _CONTROLS.pitchObject && _CONTROLS.pitchObject.rotation
    ? _CONTROLS.pitchObject.rotation.x : 0;
  append("rotation: yaw=" + formatTransformValue(p.xDire) +
    " pitch=" + formatTransformValue(pitch), "info");
}

function printServerDetails() {
  var list = (_Game && _Game.players && _Game.players.list) || [];
  var humans = list.filter(function (pl) { return pl && !pl.dummy && !pl.spectating; });
  var activeHumans = humans.filter(function (pl) { return pl.active === true; });
  var status = _Game && _Game.singlePlayer ? "offline"
    : (_io && _io.connected) ? "connected" : "disconnected";
  append("Connection: " + status, "info");
  append("Endpoint: " + location.origin, "info");
  var mapName = (_Game && _Game.map && (_Game.map.name || _Game.map.mapName)) || "(unknown)";
  append("Lobby: map " + mapName + " | mode " + ((_Game && _Game.mode) || "(unknown)"), "info");
  append("Players: " + activeHumans.length + "/" + humans.length + " | host " + location.host, "info");
}

// ── Server commands (forwarded as /slash over the chat channel). ──
// Online these go to the server. Offline (Game.singlePlayer) the ones
// with an entry in offlineServerCommands run locally instead (see
// sendServer -> maybeRunOfflineServerCommand). Commands without an
// offline handler (bhop/borders/forcecol) simply no-op offline.

var onOff = arg("on|off", "boolean", true);
var targetArg = arg("target", "string", true);

registerServer("god", [targetArg, onOff], "toggle god mode (self or target)");
registerServer("noclip", [targetArg, onOff], "toggle noclip (self or target)");
registerServer("smite", [targetArg, onOff], "toggle smite-on-hit (self or target)");
registerServer("forcehide", [targetArg, onOff], "toggle forcehide (self or target)");
registerServer("unlimitedammo", [targetArg, onOff], "toggle unlimited ammo (self or target)");
registerLocal("bhop", [arg("mode", "string", true)], "set bhop mode (off|slide|hold|always|holdjump; no arg toggles)", function (a) {
  var mod = window.bhopMod;
  if (!mod) return append("bhop mod unavailable", "error");
  var resolved = mod.resolveMode(a[0] || "", window.bhopMode | 0);
  if (resolved == null) {
    append("Usage: bhop [off|slide|hold|always|holdjump]", "error");
    return;
  }
  mod.setMode(resolved);
  var msg = "Bhop - " + mod.modeName(resolved);
  append(msg, "reply");
  // Mirror the on-screen toast the B-key cycle shows.
  if (typeof window.notify === "function") window.notify(msg);
});
registerServer("kill", [arg("target", "string", true)], "kill yourself or a target");
registerServer("end", [], "set match timer to 3 seconds");
registerServer("endless", [], "toggle endless match timer");
registerServer("borders", [onOff], "toggle invisible barriers");
registerServer("forcecol", [onOff], "toggle force collide");
// Tracks the currently-loaded map index for runtime switching (no reload).
var _curMapIndex = 0;
try { _curMapIndex = parseInt(localStorage.mapIndex, 10) || 0; } catch (e) {}

// Load a map by index at runtime via playCustomMap, no page reload.
function loadMapIndex(idx) {
  var data = window.__krunkerMapData;
  var names = window.__krunkerMaps;
  if (!Array.isArray(data) || !(idx >= 0 && idx < data.length)) {
    append("invalid map index", "error");
    return false;
  }
  if (typeof _playCustomMap !== "function") {
    // Fallback to the reload path if the runtime loader wasn't injected.
    if (typeof window.__krunkerSetMapIndex === "function") return window.__krunkerSetMapIndex(idx);
    append("map switching unavailable", "error");
    return false;
  }
  try {
    _playCustomMap(JSON.stringify(data[idx]));
    _curMapIndex = idx;
    try { localStorage.mapIndex = idx; } catch (e) {}
    var label = (Array.isArray(names) && names[idx]) ? idx + ": " + names[idx] : String(idx);
    append("Loaded " + label, "reply");
    return true;
  } catch (err) {
    append("map load failed: " + (err && err.message), "error");
    return false;
  }
}

registerLocal("maps", [], "list available maps", function () {
  var names = window.__krunkerMaps;
  if (!Array.isArray(names) || !names.length) {
    return append("no map list available", "error");
  }
  append("Maps (" + names.length + "):", "info");
  for (var i = 0; i < names.length; i++) {
    append("  " + i + ": " + names[i] + (i === _curMapIndex ? " *" : ""), "hint");
  }
});

registerLocal("map", [arg("name|index", "string", true)], "switch map at runtime (no arg = show current)", function (a) {
  var names = window.__krunkerMaps;
  if (!Array.isArray(names) || !names.length) {
    return append("no map list available", "error");
  }
  if (!a[0]) {
    append("Current map: " + (_curMapIndex >= 0 && _curMapIndex < names.length ? _curMapIndex + ": " + names[_curMapIndex] : "(unknown)"), "info");
    return;
  }
  var q = a.join(" ").trim();
  var idx = -1;
  if (/^\d+$/.test(q)) {
    idx = parseInt(q, 10);
  } else {
    var needle = q.toLowerCase();
    for (var i = 0; i < names.length; i++) {
      if (String(names[i]).toLowerCase().indexOf(needle) === 0) { idx = i; break; }
    }
  }
  if (!(idx >= 0 && idx < names.length)) {
    return append("map '" + q + "' not found; use maps to list", "error");
  }
  append("Loading " + idx + ": " + names[idx] + "...", "info");
  loadMapIndex(idx);
});

registerLocal("cycle", [], "cycle to the next map at runtime", function () {
  var count = window.__krunkerMapCount;
  if (!(count > 0)) return append("no map list available", "error");
  var next = (_curMapIndex + 1) % count;
  var names = window.__krunkerMaps;
  var label = Array.isArray(names) && names[next] ? next + ": " + names[next] : String(next);
  append("Cycling to " + label + "...", "info");
  loadMapIndex(next);
});
registerServer("mode", [arg("alias", "string", true)], "switch game mode");
registerServer("modes", [], "list game modes");
registerServer("team", [arg("red|blue", "string")], "switch teams");
registerServer("players", [], "list connected players (id, name, team)");
registerServer("ais", [], "list active AI entities (sid, name)");
registerServer("kick", [arg("name|id", "string", true)], "kick a player");
registerServer("ban", [arg("name|id", "string", true)], "ban a player account");
registerServer("score", [arg("amount", "number", true)], "add score (default +100)");

// ─── Offline command implementations ────────────────────
//
// When Game.singlePlayer is true there is no server to interpret the
// slash commands, so we run them locally against the real engine
// singletons — mirroring Krunker_v3.1.1/mod/devConsole.ts's
// offlineServerCommands table. The engine hooks these flags/methods
// consume live in src/data/players.js (noclip/god/smite/unlimitedammo)
// and index.js (forcehide/deathY). Once a real server exists it will
// interpret /noclip etc. and these offline handlers simply won't fire
// (the sendServer gate below only runs them when singlePlayer).

// Parse an on|off argument against the current value (empty = toggle).
function parseToggleArg(value, currentValue) {
  if (value == null || value === "") return !currentValue;
  var normalised = String(value).toLowerCase();
  if (normalised === "on" || normalised === "1" || normalised === "true") {
    return true;
  }
  if (normalised === "off" || normalised === "0" || normalised === "false") {
    return false;
  }
  return !currentValue;
}

// Toggle a boolean flag on the local player and echo the new state.
function offlineBoolCommand(property, label, args, onEnable) {
  var p = player();
  if (!p) return append("no local player", "error"), true;
  var next = parseToggleArg(args[0], p[property] === true);
  p[property] = next;
  if (next && typeof onEnable === "function") onEnable(p);
  append(label + " " + (next ? "ON" : "OFF"), "info");
  return true;
}

// Normalise a kill-target query for fuzzy matching.
function normaliseOfflineTarget(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

// Resolve a /kill target (player) offline by name/id, exact then prefix.
function resolveOfflineKillTarget(query) {
  var needle = normaliseOfflineTarget(query);
  if (!needle) return { target: null, reason: "empty" };
  var list = (_Game && _Game.players && _Game.players.list) || [];
  var exact = [];
  var partial = [];
  for (var i = 0; i < list.length; i++) {
    var c = list[i];
    if (!c || c.dummy || c.active !== true) continue;
    var keys = [c.name, c.alias, c.id]
      .filter(Boolean)
      .map(function (v) { return normaliseOfflineTarget(v); });
    if (keys.indexOf(needle) !== -1) {
      exact.push(c);
    } else if (keys.some(function (v) { return v.indexOf(needle) === 0; })) {
      partial.push(c);
    }
  }
  if (exact.length === 1) return { target: exact[0], reason: null };
  if (exact.length > 1) return { target: null, reason: "ambiguous" };
  if (partial.length === 1) return { target: partial[0], reason: null };
  if (partial.length > 1) return { target: null, reason: "ambiguous" };
  return { target: null, reason: "not-found" };
}

// Kill a player offline, temporarily lifting god mode so the kill lands.
function killOfflinePlayer(source, target) {
  if (!_Game || !target) return false;
  var hadGod = target.godMode === true;
  try {
    if (hadGod) target.godMode = false;
    if (_Game.players && typeof _Game.players.kill === "function") {
      _Game.players.kill(target, source, {});
      return true;
    }
    if (_Game.players && typeof _Game.players.changeHealth === "function") {
      _Game.players.changeHealth(target, source, 1e6, true);
      return true;
    }
    target.active = false;
    target.health = 0;
    return true;
  } finally {
    if (hadGod) target.godMode = true;
  }
}

var offlineServerCommands = {
  god: function (args) {
    return offlineBoolCommand("godMode", "God mode", args, function (p) {
      if (Number.isFinite(p.maxHealth)) p.health = p.maxHealth;
    });
  },
  noclip: function (args) {
    return offlineBoolCommand("noclip", "Noclip", args);
  },
  smite: function (args) {
    return offlineBoolCommand("smite", "Smite", args);
  },
  forcehide: function (args) {
    return offlineBoolCommand("forceHide", "Forcehide", args);
  },
  unlimitedammo: function (args) {
    return offlineBoolCommand("unlimitedAmmo", "Unlimited ammo", args);
  },
  kill: function (args) {
    var p = player();
    if (!p) return append("no local player", "error"), true;
    var query = args.join(" ").trim();
    if (!query) {
      killOfflinePlayer(p, p);
      append("You killed yourself.", "info");
      return true;
    }
    var resolved = resolveOfflineKillTarget(query);
    if (!resolved.target) {
      append(
        resolved.reason === "ambiguous"
          ? "Kill target '" + query + "' is ambiguous."
          : "Kill target '" + query + "' not found.",
        "error"
      );
      return true;
    }
    killOfflinePlayer(p, resolved.target);
    append(
      "Killed " + (resolved.target.name || resolved.target.alias || resolved.target.id) + ".",
      "info"
    );
    return true;
  },
  score: function (args) {
    var p = player();
    if (!p || !_Game || !_Game.players || typeof _Game.players.score !== "function") {
      return true;
    }
    var amount = args.length ? Number(args[0]) : 100;
    if (!Number.isFinite(amount) || amount === 0) {
      append("Usage: score <amount> (default 100).", "error");
      return true;
    }
    _Game.players.score(p, amount);
    append((amount > 0 ? "+" : "") + amount + " score (total " + p.score + ").", "info");
    return true;
  },
  team: function (args) {
    var p = player();
    var mode = _Game && _Game.mode;
    if (!p || !_Game) return true;
    if (!mode || !mode.teams || mode.friendly || mode.clanWar || mode.convTeam) {
      append("/team only works in team modes.", "error");
      return true;
    }
    var a = (args[0] || "").toLowerCase();
    var next = a === "red" || a === "1" ? 1 : a === "blue" || a === "2" ? 2 : null;
    if (next == null) {
      append("Usage: /team red|blue", "error");
      return true;
    }
    if (p.team === next) {
      append("Already on " + a + ".", "info");
      return true;
    }
    p.team = next;
    killOfflinePlayer(p, p);
    append("Switched to " + a + " team.", "info");
    return true;
  },
  teams: function () {
    var list = (_Game && _Game.players && _Game.players.list) || [];
    var mode = _Game && _Game.mode;
    if (!list.length) {
      append("No players.", "info");
      return true;
    }
    if (!mode || !mode.teams || mode.convTeam) {
      append("Not a team mode.", "info");
      return true;
    }
    var groups = {};
    for (var i = 0; i < list.length; i++) {
      var pl = list[i];
      if (!pl || pl.spectating) continue;
      var t = pl.team || 0;
      (groups[t] = groups[t] || []).push(pl.name || "Guest_" + pl.sid);
    }
    var teamKeys = Object.keys(groups).map(Number).sort(function (l, r) { return l - r; });
    for (var k = 0; k < teamKeys.length; k++) {
      var team = teamKeys[k];
      var label = team === 1 ? "Red" : team === 2 ? "Blue" : "Team " + team;
      var names = groups[team];
      append(label + " (" + names.length + "): " + names.join(", "), "info");
    }
    return true;
  },
  end: function () {
    if (!_Game) return true;
    _Game.endTimer = 0;
    _Game.gameTimer = 3e3;
    _Game.lastTimer = null;
    append("Match timer set to 3 seconds.", "info");
    return true;
  },
  endless: function () {
    if (!_Game) return true;
    var isEndless = _Game.endTimer === "inf";
    if (isEndless) {
      var restored =
        _Game.forceStaticMap === true
          ? 240000
          : _Game.mode && _Game.mode.gameTime == null
            ? (_Game.config && _Game.config.gameTime ? _Game.config.gameTime : 4) * 60000
            : _Game.mode.gameTime;
      _Game.endTimer = 0;
      _Game.gameTimer = restored;
      _Game.lastTimer = null;
      append("Match timer resumed.", "info");
      return true;
    }
    _Game.endTimer = "inf";
    _Game.lastTimer = null;
    append("Match timer paused.", "info");
    return true;
  }
};

// Run an offline handler for a raw slash line; returns true if handled.
function maybeRunOfflineServerCommand(line) {
  if (!_Game || !_Game.singlePlayer) return false;
  var stripped = line.charAt(0) === "/" ? line.slice(1) : line;
  var space = stripped.indexOf(" ");
  var name = (space === -1 ? stripped : stripped.slice(0, space)).toLowerCase();
  var rest = space === -1 ? "" : stripped.slice(space + 1);
  var handler = offlineServerCommands[name];
  if (!handler) return false;
  try {
    handler(parseArgs(rest), rest);
  } catch (err) {
    append("error: " + (err && err.message), "error");
  }
  return true;
}

// ─── Exec / dispatch ────────────────────────────────────

function sendServer(line) {
  // Offline: run the command locally against the engine.
  if (maybeRunOfflineServerCommand(line)) {
    return;
  }
  if (!_io || typeof _io.send !== "function") {
    append("not connected to server", "error");
    return;
  }
  try {
    _io.send("c", line);
  } catch (err) {
    append("send failed: " + (err && err.message), "error");
  }
}

function parseArgs(rest) {
  var out = [];
  var re = /"([^"]*)"|(\S+)/g;
  var m;
  while ((m = re.exec(rest)) !== null) {
    out.push(m[1] !== undefined ? m[1] : m[2]);
  }
  return out;
}

function exec(line) {
  line = line.trim();
  if (!line) return;
  append("] " + line, "cmd");
  var stripped = line.charAt(0) === "/" ? line.slice(1) : line;
  var space = stripped.indexOf(" ");
  var name = (space === -1 ? stripped : stripped.slice(0, space)).toLowerCase();
  var rest = space === -1 ? "" : stripped.slice(space + 1);
  var args = parseArgs(rest);

  var c = cmds[name];
  if (!c) return;

  if (c.kind === "local") {
    try {
      c.run(args, rest);
    } catch (err) {
      append("error: " + (err && err.message), "error");
    }
  } else {
    sendServer("/" + stripped);
  }
}

// ─── Autocomplete / hints ───────────────────────────────

function fmtArgs(args) {
  return args.map(function (a) {
    return a.optional ? a.name + ": " + a.type + "?" : a.name + ": " + a.type;
  }).join("  ");
}

function clearHints() {
  hints.innerHTML = "";
  hints.classList.remove("isOpen");
}

function addHintItem(label, meta, active, onPick) {
  var item = document.createElement("div");
  item.className = "hintItem" + (active ? " isActive" : "");
  item.textContent = label;
  if (meta) {
    var span = document.createElement("span");
    span.className = "hintMeta";
    span.textContent = "  " + meta;
    item.append(span);
  }
  if (onPick) {
    item.addEventListener("mousedown", function (e) {
      e.preventDefault();
      onPick();
      input.focus();
    });
  }
  hints.append(item);
}

function updateHints() {
  var v = input.value;
  clearHints();
  if (!v) return;
  var space = v.indexOf(" ");
  var head = (space === -1 ? v : v.slice(0, space)).toLowerCase();
  var headStripped = head.charAt(0) === "/" ? head.slice(1) : head;

  if (space === -1) {
    var matches = Object.keys(cmds).filter(function (n) {
      return n.indexOf(headStripped) === 0;
    }).slice(0, 12);
    if (!matches.length) return;
    hints.classList.add("isOpen");
    var activeIndex = Math.max(0, matches.indexOf(headStripped));
    for (var i = 0; i < matches.length; i++) {
      (function (name, idx) {
        addHintItem(name, null, idx === activeIndex, function () {
          input.value = name + " ";
          updateHints();
        });
      })(matches[i], i);
    }
    return;
  }

  var c = cmds[headStripped];
  hints.classList.add("isOpen");
  if (c) {
    addHintItem(headStripped, fmtArgs(c.args) + "  —  " + c.desc, true);
  } else {
    addHintItem("(unknown command)", "will not be sent", true);
  }
}

function tabComplete() {
  var v = input.value;
  var space = v.indexOf(" ");
  if (space !== -1) return;
  var headStripped = v.charAt(0) === "/" ? v.slice(1) : v;
  var matches = Object.keys(cmds).filter(function (n) {
    return n.indexOf(headStripped) === 0;
  });
  if (matches.length === 1) {
    input.value = matches[0] + " ";
  } else if (matches.length > 1) {
    var prefix = matches[0];
    for (var i = 0; i < matches.length; i++) {
      while (matches[i].indexOf(prefix) !== 0) prefix = prefix.slice(0, -1);
    }
    if (prefix.length > headStripped.length) input.value = prefix;
  }
  updateHints();
}

// ─── Input handling ─────────────────────────────────────

function handleConsoleKeydown(e) {
  if (!visible) return;
  var activeEl = document.activeElement;
  var focusInConsole = activeEl === input || activeEl === logs;

  if (e.key === "Escape" && focusInConsole) {
    e.preventDefault();
    e.stopPropagation();
    toggleConsole();
    return;
  }

  if (activeEl !== input) return;

  if (e.key === "ArrowUp") {
    e.preventDefault();
    e.stopPropagation();
    if (!history.length) return;
    if (historyIdx === -1) {
      draft = input.value;
      historyIdx = history.length - 1;
    } else if (historyIdx > 0) {
      historyIdx--;
    }
    input.value = history[historyIdx];
    updateHints();
  } else if (e.key === "ArrowDown") {
    e.preventDefault();
    e.stopPropagation();
    if (historyIdx === -1) return;
    if (historyIdx < history.length - 1) {
      historyIdx++;
      input.value = history[historyIdx];
    } else {
      historyIdx = -1;
      input.value = draft;
    }
    updateHints();
  } else if (e.key === "Tab") {
    e.preventDefault();
    e.stopPropagation();
    tabComplete();
  }
}

window.addEventListener("keydown", handleConsoleKeydown, true);

// `input` event fires after the char is inserted, surviving upstream
// stopImmediatePropagation. Strip a typed backtick/tilde and toggle.
input.addEventListener("input", function (e) {
  var data = e.data;
  if (data && (data === "`" || data === "~")) {
    var pos = input.selectionStart != null ? input.selectionStart : input.value.length;
    var before = input.value.slice(0, Math.max(0, pos - data.length));
    var after = input.value.slice(pos);
    input.value = before + after;
    try { input.setSelectionRange(before.length, before.length); } catch (err) {}
    toggleConsole();
    return;
  }
  updateHints();
});

inputRow.addEventListener("submit", function (e) {
  e.preventDefault();
  e.stopPropagation();
  var line = input.value;
  input.value = "";
  clearHints();
  if (line.trim()) {
    history.push(line);
    if (history.length > 200) history.shift();
  }
  historyIdx = -1;
  draft = "";
  exec(line);
});

["keydown", "keyup", "keypress"].forEach(function (t) {
  overlay.addEventListener(t, function (e) { e.stopPropagation(); }, true);
});

// ─── Drag / resize ──────────────────────────────────────

var GEOM_KEY = "krk_dev_console_geom";
function loadGeom() {
  try {
    var raw = localStorage.getItem(GEOM_KEY);
    if (!raw) return null;
    var g = JSON.parse(raw);
    if (!g || typeof g !== "object") return null;
    return g;
  } catch (e) { return null; }
}
var saveGeomTimer = 0;
function saveGeom() {
  clearTimeout(saveGeomTimer);
  saveGeomTimer = setTimeout(function () {
    if (modal.dataset.posCommitted !== "1") return;
    try {
      localStorage.setItem(GEOM_KEY, JSON.stringify({
        left: parseInt(modal.style.left || "0", 10),
        top: parseInt(modal.style.top || "0", 10),
        width: modal.offsetWidth,
        height: modal.offsetHeight,
      }));
    } catch (e) {}
  }, 150);
}

function clampPos() {
  var w = modal.offsetWidth;
  var h = modal.offsetHeight;
  var left = parseInt(modal.style.left || "0", 10);
  var top = parseInt(modal.style.top || "0", 10);
  var maxX = Math.max(0, window.innerWidth - w);
  var maxY = Math.max(0, window.innerHeight - h);
  modal.style.left = Math.max(0, Math.min(maxX, left)) + "px";
  modal.style.top = Math.max(0, Math.min(maxY, top)) + "px";
}

function commitInitialPos() {
  if (modal.dataset.posCommitted === "1") return;
  var rect = modal.getBoundingClientRect();
  modal.style.transform = "none";
  modal.style.left = rect.left + "px";
  modal.style.top = rect.top + "px";
  modal.dataset.posCommitted = "1";
}

function restoreGeom() {
  var g = loadGeom();
  if (!g) return;
  if (Number.isFinite(g.width) && g.width >= 280) modal.style.width = g.width + "px";
  if (Number.isFinite(g.height) && g.height >= 160) modal.style.height = g.height + "px";
  if (Number.isFinite(g.left) && Number.isFinite(g.top)) {
    modal.style.transform = "none";
    modal.style.left = g.left + "px";
    modal.style.top = g.top + "px";
    modal.dataset.posCommitted = "1";
    clampPos();
  }
}
if (document.body) restoreGeom();
else document.addEventListener("DOMContentLoaded", restoreGeom, { once: true });

(function () {
  var dragging = false, dx = 0, dy = 0;
  header.addEventListener("mousedown", function (e) {
    if (e.button !== 0 || e.target === closeBtn) return;
    commitInitialPos();
    dragging = true;
    var rect = modal.getBoundingClientRect();
    dx = e.clientX - rect.left;
    dy = e.clientY - rect.top;
    e.preventDefault();
  });
  window.addEventListener("mousemove", function (e) {
    if (!dragging) return;
    modal.style.left = e.clientX - dx + "px";
    modal.style.top = e.clientY - dy + "px";
    clampPos();
  });
  window.addEventListener("mouseup", function () {
    if (dragging) saveGeom();
    dragging = false;
  });
})();

(function () {
  var resizing = false, startX = 0, startY = 0, startW = 0, startH = 0;
  grip.addEventListener("mousedown", function (e) {
    if (e.button !== 0) return;
    commitInitialPos();
    resizing = true;
    startX = e.clientX;
    startY = e.clientY;
    startW = modal.offsetWidth;
    startH = modal.offsetHeight;
    e.preventDefault();
  });
  window.addEventListener("mousemove", function (e) {
    if (!resizing) return;
    var w = Math.max(280, startW + (e.clientX - startX));
    var h = Math.max(160, startH + (e.clientY - startY));
    modal.style.width = w + "px";
    modal.style.height = h + "px";
  });
  window.addEventListener("mouseup", function () {
    if (resizing) saveGeom();
    resizing = false;
  });
})();

// ─── Visibility ─────────────────────────────────────────

var visible = false;

function update(goToGame) {
  if (goToGame === undefined) goToGame = true;
  overlay.style.display = visible ? "block" : "none";
  if (visible) {
    setTimeout(function () { input.focus(); }, 0);
    // Release pointer lock the v1.5.2 way.
    if (_CONTROLS && _CONTROLS.hasPointerlock) _CONTROLS.toggle(false);
    else {
      try { document.exitPointerLock && document.exitPointerLock(); } catch (e) {}
    }
  } else if (goToGame) {
    input.blur();
    if (_CONTROLS) _CONTROLS.toggle(true);
  }
}

function toggleConsole(goToGame) {
  visible = !visible;
  update(goToGame);
}

// ─── Server reply mirroring ─────────────────────────────
//
// v1.5.2 has no io.events property; the event table is passed into
// init() (deps.events, the io.connect literal from index.js). Wrap
// its `ch` (addChat) handler so server chat replies mirror here.

function hookEvents(events) {
  if (!events) return;
  _events = events;
  var orig = events.ch;
  if (typeof orig !== "function" || orig.__devConsoleWrapped) return;
  var wrapped = function () {
    try {
      var name = arguments[0];
      var text = arguments[1];
      var cleaned = String(text == null ? "" : text).replace(/<[^>]+>/g, "");
      append((name || "Server") + ": " + cleaned, "reply");
    } catch (e) {}
    return orig.apply(this, arguments);
  };
  wrapped.__devConsoleWrapped = true;
  events.ch = wrapped;
}

// ─── Key bind dispatch ──────────────────────────────────

document.addEventListener("keydown", function (e) {
  if (e.repeat) return;
  if (visible) return;
  var tag = e.target instanceof Element ? e.target.tagName : "";
  if (tag === "INPUT" || tag === "TEXTAREA") return;
  var cmd = keyBinds[e.key] || keyBinds[e.code];
  if (cmd) {
    e.preventDefault();
    exec(cmd);
  }
});

// ─── Bootstrap banner ───────────────────────────────────

append("Krunker developer console - type `help` for commands.", "info");
append("Press ~ to toggle, Esc to close, Tab to autocomplete.", "hint");

// ─── Exports ────────────────────────────────────────────

module.exports = {
  init: function (deps) {
    window.Game = _Game = deps.Game;
    _getPlayer = deps.getPlayer || function () { return null; };
    _CONTROLS = deps.CONTROLS;
    _config = deps.config;
    _io = deps.io;
    _RENDER = deps.RENDER;
    _UI = deps.UI;
    _playCustomMap = deps.playCustomMap;
    if (deps.events) hookEvents(deps.events);
    window.devConsole = {
      exec: exec,
      print: print,
      toggle: toggleConsole,
      update: update,
      hookEvents: hookEvents,
      get visible() { return visible; },
      set visible(v) { visible = !!v; },
    };
  },
  exec: exec,
  print: print,
  toggle: toggleConsole,
  update: update,
  hookEvents: hookEvents,
};
